// Collection of Admin Authentication API Errors and their error messages. This list can grow.

import { ServiceManager } from "../../services/service-manager";
import { severity } from "../models/severity.enum";

export enum authErrorsEnum {
    authClaimsTooLarge = 'auth/claims-too-large',
    authEmailAlreadyExists = 'auth/email-already-exists',
    authIdTokenExpired = 'auth/id-token-expired',
    authIdTokenRevoked = 'auth/id-token-revoked',
    authInsufficientPermission = 'auth/insufficient-permission',
    authInternalError = 'auth/internal-error',
    authInvalidArgumen = 'auth/invalid-argumen',
    authInvalidClaims = 'auth/invalid-claims',
    authInvalidContinueUri = 'auth/invalid-continue-uri',
    authInvalidCreationTime = 'auth/invalid-creation-time',
    authInvalidCredential = ' auth/invalid-credential',
    authInvalidDisabledField = 'auth/invalid-disabled-field',
    authInvalidDisplayName = 'auth/invalid-display-name',
    authInvalidDynamicLinkDomain = 'auth/invalid-dynamic-link-domain',
    authInvalidEmail = 'auth/invalid-email',
    authInvalidEmailVerified = 'auth/invalid-email-verified',
    authInvalidHashAlgorithm = 'auth/invalid-hash-algorithm',
    authInvalidHashBlockSize = 'auth/invalid-hash-block-size',
    authInvalidHashDerivedKeyLength = 'auth/invalid-hash-derived-key-length',
    authInvalidHashKey = 'auth/invalid-hash-key',
    authInvalidHashMemoryCost = 'auth/invalid-hash-memory-cost',
    authInvalidHashParallelization = ' auth/invalid-hash-parallelization',
    authInvalidHashRounds = 'auth/invalid-hash-rounds',
    authInvalidHashSaltSeparator = 'auth/invalid-hash-salt-separator',
    authInvalidIdToken = 'auth/invalid-id-token',
    authInvalidLastSignInTime = 'auth/invalid-last-sign-in-time',
    authInvalidPageToken = 'auth/invalid-page-token',
    authInvalidPassword = 'auth/invalid-password',
    authInvalidPasswordHash = 'auth/invalid-password-hash',
    authInvalidPasswordSalt = 'auth/invalid-password-salt',
    authInvalidPhoneNumber = 'auth/invalid-phone-number',
    authInvalidPhotoUrl = 'auth/invalid-photo-url',
    authInvalidProviderData = 'auth/invalid-provider-data',
    authInvalidProviderId = 'auth/invalid-provider-id',
    authInvalidOauthResponsetype = 'auth/invalid-oauth-responsetype',
    authInvalidSessionCookieDuration = 'auth/invalid-session-cookie-duration',
    authInvalidUid = 'auth/invalid-uid',
    authInvalidUserImport = 'auth/invalid-user-import',
    authMaximumUserCountExceeded = 'auth/maximum-user-count-exceeded',
    authMissingAndroidPkgName = 'auth/missing-android-pkg-name',
    authMissingContinueUri = 'auth/missing-continue-uri',
    authMissingHashAlgorithm = 'auth/missing-hash-algorithm',
    authMissingIosBundleId = 'auth/missing-ios-bundle-id',
    authMissingUid = 'auth/missing-uid',
    authMissingOauthClientSecret = 'auth/missing-oauth-client-secret',
    authOperationNotAllowed = 'auth/operation-not-allowed',
    authPhoneNumberAlreadyExists = 'auth/phone-number-already-exists',
    authProjectNotFound = 'auth/project-not-found',
    authReservedClaims = 'auth/reserved-claims',
    authSessionCookieExpired = 'auth/session-cookie-expired',
    authSessionCookieRevoked = 'auth/session-cookie-revoked',
    authUidAlreadyExists = 'auth/uid-already-exists',
    authUnauthorizedContinueUri = 'auth/unauthorized-continue-uri',
    authUserNotFound = 'auth/user-not-found',
    authUserDisabled = 'auth/user-disabled',
    authWrongPassword = 'auth/wrong-password',
    authEmailAlreadyInUse = 'auth/email-already-in-use',
    authInvalidActionCode = 'auth/invalid-action-code',
}

export interface AuthError {
    code: authErrorsEnum,
    languageMessages: {
        nl: string,
        en: string,
    }
}

const errors: AuthError[] = [
    {
        code: authErrorsEnum.authClaimsTooLarge,
        languageMessages: {
            nl: '',
            en: 'The claims payload provided to setCustomUserClaims() exceeds the maximum allowed size of 1000 bytes.'
        }
    },
    {
        code: authErrorsEnum.authEmailAlreadyExists,
        languageMessages: {
            nl: '',
            en: 'The provided email is already in use by an existing user. Each user must have a unique email.'
        }
    },
    {
        code: authErrorsEnum.authIdTokenExpired,
        languageMessages: {
            nl: '',
            en: 'The provided Firebase ID token is expired.'
        }
    },
    {
        code: authErrorsEnum.authIdTokenRevoked,
        languageMessages: {
            nl: '',
            en: 'The provided Firebase ID token has been revoked.'
        }
    },
    {
        code: authErrorsEnum.authInsufficientPermission,
        languageMessages: {
            nl: '',
            en: 'The credential used to initialize the Admin SDK has insufficient permission to access the requested Authentication resource.'
        }
    },
    {
        code: authErrorsEnum.authInternalError,
        languageMessages: {
            nl: 'Er is interne server fout opgetreden.',
            en: 'The Authentication server encountered an unexpected error while trying to process the request.'
        }
    },
    {
        code: authErrorsEnum.authInvalidArgumen,
        languageMessages: {
            nl: '',
            en: 'An invalid argument was provided to an Authentication method. The error message should contain additional information.'
        }
    },
    {
        code: authErrorsEnum.authInvalidClaims,
        languageMessages: {
            nl: '',
            en: 'The custom claim attributes provided to setCustomUserClaims() are invalid.'
        }
    },
    {
        code: authErrorsEnum.authInvalidContinueUri,
        languageMessages: {
            nl: '',
            en: 'The continue URL must be a valid URL string.'
        }
    },
    {
        code: authErrorsEnum.authInvalidCreationTime,
        languageMessages: {
            nl: '',
            en: 'The creation time must be a valid UTC date string.'
        }
    },
    {
        code: authErrorsEnum.authInvalidCredential,
        languageMessages: {
            nl: '',
            en: 'The credential used to authenticate the Admin SDKs cannot be used to perform the desired action. Certain Authentication methods such as createCustomToken() and verifyIdToken() require the SDK to be initialized with a certificate credential as opposed to a refresh token or Application Default credential. See Initialize the SDK for documentation on how to authenticate the Admin SDKs with a certificate credential.'
        }
    },
    {
        code: authErrorsEnum.authInvalidDisabledField,
        languageMessages: {
            nl: '',
            en: 'The provided value for the disabled user property is invalid. It must be a boolean.'
        }
    },
    {
        code: authErrorsEnum.authInvalidDisplayName,
        languageMessages: {
            nl: '',
            en: 'The provided value for the displayName user property is invalid. It must be a non-empty string.'
        }
    },
    {
        code: authErrorsEnum.authInvalidDynamicLinkDomain,
        languageMessages: {
            nl: '',
            en: 'The provided dynamic link domain is not configured or authorized for the current project.'
        }
    },
    {
        code: authErrorsEnum.authInvalidEmail,
        languageMessages: {
            nl: 'Het e-mailadres is niet correct.',
            en: 'The provided value for the email user property is invalid. It must be a string email address.'
        }
    },
    {
        code: authErrorsEnum.authInvalidEmailVerified,
        languageMessages: {
            nl: '',
            en: 'The provided value for the emailVerified user property is invalid. It must be a boolean.'
        }
    },
    {
        code: authErrorsEnum.authInvalidHashAlgorithm,
        languageMessages: {
            nl: '',
            en: 'The hash algorithm must match one of the strings in the list of supported algorithms.'
        }
    },
    {
        code: authErrorsEnum.authInvalidHashBlockSize,
        languageMessages: {
            nl: '',
            en: 'The hash block size must be a valid number.'
        }
    },
    {
        code: authErrorsEnum.authInvalidHashDerivedKeyLength,
        languageMessages: {
            nl: '',
            en: 'The hash derived key length must be a valid number.'
        }
    },
    {
        code: authErrorsEnum.authInvalidHashKey,
        languageMessages: {
            nl: '',
            en: 'The hash key must a valid byte buffer.'
        }
    },
    {
        code: authErrorsEnum.authInvalidHashMemoryCost,
        languageMessages: {
            nl: '',
            en: 'The hash memory cost must be a valid number.'
        }
    },
    {
        code: authErrorsEnum.authInvalidHashRounds,
        languageMessages: {
            nl: '',
            en: 'The hash rounds must be a valid number.'
        }
    },
    {
        code: authErrorsEnum.authInvalidHashSaltSeparator,
        languageMessages: {
            nl: '',
            en: 'The hashing algorithm salt separator field must be a valid byte buffer.'
        }
    },
    {
        code: authErrorsEnum.authInvalidIdToken,
        languageMessages: {
            nl: '',
            en: 'The provided ID token is not a valid Firebase ID token.'
        }
    },
    {
        code: authErrorsEnum.authInvalidLastSignInTime,
        languageMessages: {
            nl: '',
            en: 'The last sign-in time must be a valid UTC date string.'
        }
    },
    {
        code: authErrorsEnum.authInvalidPageToken,
        languageMessages: {
            nl: '',
            en: 'The provided next page token in listUsers() is invalid. It must be a valid non-empty string.'
        }
    },
    {
        code: authErrorsEnum.authInvalidPassword,
        languageMessages: {
            nl: 'Het wachtwoord moet minimaal 6 karakters hebben.',
            en: 'The provided value for the password user property is invalid. It must be a string with at least six characters.'
        }
    },
    {
        code: authErrorsEnum.authInvalidPasswordHash,
        languageMessages: {
            nl: '',
            en: 'The password hash must be a valid byte buffer.'
        }
    },
    {
        code: authErrorsEnum.authInvalidPasswordSalt,
        languageMessages: {
            nl: '',
            en: 'The password hash must be a valid byte buffer.'
        }
    },
    {
        code: authErrorsEnum.authInvalidPhoneNumber,
        languageMessages: {
            nl: '',
            en: 'The provided value for the phoneNumber is invalid. It must be a non-empty E.164 standard compliant identifier string.'
        }
    },
    {
        code: authErrorsEnum.authInvalidPhotoUrl,
        languageMessages: {
            nl: '',
            en: 'The provided value for the photoURL user property is invalid. It must be a string URL.'
        }
    },
    {
        code: authErrorsEnum.authInvalidProviderData,
        languageMessages: {
            nl: '',
            en: 'The providerData must be a valid array of UserInfo objects.'
        }
    },
    {
        code: authErrorsEnum.authInvalidProviderId,
        languageMessages: {
            nl: '',
            en: 'The providerId must be a valid supported provider identifier string.'
        }
    },
    {
        code: authErrorsEnum.authInvalidOauthResponsetype,
        languageMessages: {
            nl: '',
            en: 'Only exactly one OAuth responseType should be set to true.'
        }
    },
    {
        code: authErrorsEnum.authInvalidSessionCookieDuration,
        languageMessages: {
            nl: '',
            en: 'The session cookie duration must be a valid number in milliseconds between 5 minutes and 2 weeks.'
        }
    },
    {
        code: authErrorsEnum.authInvalidUid,
        languageMessages: {
            nl: '',
            en: 'The provided uid must be a non-empty string with at most 128 characters.'
        }
    },
    {
        code: authErrorsEnum.authInvalidUserImport,
        languageMessages: {
            nl: '',
            en: 'The user record to import is invalid.'
        }
    },
    {
        code: authErrorsEnum.authMaximumUserCountExceeded,
        languageMessages: {
            nl: '',
            en: 'The maximum allowed number of users to import has been exceeded.'
        }
    },
    {
        code: authErrorsEnum.authMissingAndroidPkgName,
        languageMessages: {
            nl: '',
            en: 'An Android Package Name must be provided if the Android App is required to be installed.'
        }
    },
    {
        code: authErrorsEnum.authMissingContinueUri,
        languageMessages: {
            nl: '',
            en: 'A valid continue URL must be provided in the request.'
        }
    },
    {
        code: authErrorsEnum.authMissingHashAlgorithm,
        languageMessages: {
            nl: '',
            en: 'Importing users with password hashes requires that the hashing algorithm and its parameters be provided.'
        }
    },
    {
        code: authErrorsEnum.authMissingIosBundleId,
        languageMessages: {
            nl: '',
            en: 'The request is missing a Bundle ID.'
        }
    },
    {
        code: authErrorsEnum.authMissingUid,
        languageMessages: {
            nl: '',
            en: 'A uid identifier is required for the current operation.'
        }
    },
    {
        code: authErrorsEnum.authMissingOauthClientSecret,
        languageMessages: {
            nl: '',
            en: 'The OAuth configuration client secret is required to enable OIDC code flow.'
        }
    },
    {
        code: authErrorsEnum.authOperationNotAllowed,
        languageMessages: {
            nl: '',
            en: 'The provided sign-in provider is disabled for your Firebase project. Enable it from the Sign-in Method section of the Firebase console.'
        }
    },
    {
        code: authErrorsEnum.authPhoneNumberAlreadyExists,
        languageMessages: {
            nl: '',
            en: 'The provided phoneNumber is already in use by an existing user. Each user must have a unique phoneNumber.'
        }
    },
    {
        code: authErrorsEnum.authProjectNotFound,
        languageMessages: {
            nl: '',
            en: 'No Firebase project was found for the credential used to initialize the Admin SDKs. Refer to Set up a Firebase project for documentation on how to generate a credential for your project and use it to authenticate the Admin SDKs.'
        }
    },
    {
        code: authErrorsEnum.authReservedClaims,
        languageMessages: {
            nl: '',
            en: 'One or more custom user claims provided to setCustomUserClaims() are reserved. For example, OIDC specific claims such as (sub, iat, iss, exp, aud, auth_time, etc) should not be used as keys for custom claims.'
        }
    },
    {
        code: authErrorsEnum.authSessionCookieExpired,
        languageMessages: {
            nl: '',
            en: 'The provided Firebase session cookie is expired.'
        }
    },
    {
        code: authErrorsEnum.authSessionCookieRevoked,
        languageMessages: {
            nl: '',
            en: 'The provided Firebase session cookie has been revoked.'
        }
    },
    {
        code: authErrorsEnum.authUidAlreadyExists,
        languageMessages: {
            nl: '',
            en: 'The provided uid is already in use by an existing user. Each user must have a unique uid.'
        }
    },
    {
        code: authErrorsEnum.authUnauthorizedContinueUri,
        languageMessages: {
            nl: '',
            en: 'The domain of the continue URL is not whitelisted. Whitelist the domain in the Firebase Console.'
        }
    },
    {
        code: authErrorsEnum.authUserNotFound,
        languageMessages: {
            nl: 'Er is geen gebruiker gevonden met opgegeven identifier.',
            en: 'There is no existing user record corresponding to the provided identifier.'
        }
    },
    {
        code: authErrorsEnum.authUserNotFound,
        languageMessages: {
            nl: 'Gebruiker niet gevonden.',
            en: 'There is no existing user record corresponding to the provided identifier.'
        }
    },
    {
        code: authErrorsEnum.authUserDisabled,
        languageMessages: {
            nl: 'Deze gebruiker is gediskwalificeerd. Als u denkt dat het fout is, neem kontakt met ons op.',
            en: ' The user corresponding to the given email has been disabled.'
        }
    },
    {
        code: authErrorsEnum.authWrongPassword,
        languageMessages: {
            nl: 'Het wachtwoord is ongeldig voor het opgegeven e-mailadres.',
            en: 'The password is invalid for the given email, or the account corresponding to the email does not have a password set.'
        }
    },
    {
        code: authErrorsEnum.authEmailAlreadyInUse,
        languageMessages: {
            nl: 'Het e-mailadres is al in gebruik.',
            en: 'The email is already in use.'
        }
    },
    {
        code: authErrorsEnum.authInvalidActionCode,
        languageMessages: {
            nl: 'Ongeldige actie code.',
            en: 'The action code is invalid.'
        }
    }
]

export const getAuthErrorMessageForCode = (errorCode: authErrorsEnum): string => {
    let errorMessage = `Onbekende fout opgetreden met code "${errorCode}".`;
    const e = errors.find(e => e.code === errorCode);
    if (e) {
        errorMessage = e.languageMessages.nl;
        if (!errorMessage) {
            errorMessage = e.languageMessages.en;
        }
    }
    return errorMessage;
}

export const handleError = (err: { code?: authErrorsEnum, message?: string }): void => {
    if (err.code) {
        ServiceManager.snackbarService.openSnackbar(getAuthErrorMessageForCode(err.code), severity.error);
    } else if (err.message) {
        ServiceManager.snackbarService.openSnackbar('Er is iets misgegaan: ' + err.message, severity.error);
    } else {
        ServiceManager.snackbarService.openSnackbar('Er is iets misgegaan.', severity.error);
    }
}